import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Plus, Settings, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { ImageLightbox } from '@/components/shared/image-lightbox';
import { PageHeader } from '@/components/shared/page-header';
import { PermissionGate } from '@/components/shared/permission-gate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS } from '@/lib/permissions';
import {
  compressGalleryPhoto,
  GalleryPhotoInvalidError,
  GalleryPhotoTooLargeError,
} from '@/lib/compress-gallery-photo';
import { uploadBlob } from '@/lib/upload';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';
import type { GalleryCategory } from '@/pages/content/gallery-categories-dialog';

interface Gallery {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  status: 'draft' | 'scheduled' | 'published';
  publishAt: string | null;
  publishedAt: string | null;
  publishedOn: string;
  coverPhotoId: string | null;
  photoCount: number;
  coverThumbnailUrl: string | null;
  coverMainColor: string | null;
  coverAspectRatio: number | null;
  createdAt: string;
  canDelete?: boolean;
}

interface GalleryPhoto {
  id: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
  sortOrder: number;
  width: number | null;
  height: number | null;
  aspectRatio: number | null;
  mainColor: string | null;
}

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';

const GalleryAddPhotosButton = ({
  disabled,
  label,
  onFiles,
}: {
  disabled?: boolean;
  label: string;
  onFiles: (files: File[]) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptFiles = (candidates: FileList | null) => {
    if (!candidates || disabled) return;
    const list = Array.from(candidates).filter((file) => file.type.startsWith('image/'));
    if (list.length > 0) onFiles(list);
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    acceptFiles(event.target.files);
    event.target.value = '';
  };

  return (
    <>
      <Button
        type="button"
        leadingIcon={<Plus className="size-4" aria-hidden />}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        multiple
        disabled={disabled}
        onChange={onInputChange}
        className="sr-only"
      />
    </>
  );
};

export const GalleryDetailPage = () => {
  const { galleryId = '' } = useParams<{ galleryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>();
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftPublishedOn, setDraftPublishedOn] = useState('');

  const gallery = useQuery({
    queryKey: ['content', 'galleries', galleryId],
    queryFn: () => api.get<Gallery>(`/content/galleries/${galleryId}`),
    enabled: Boolean(galleryId),
  });

  const categories = useQuery({
    queryKey: ['content', 'gallery-categories'],
    queryFn: () =>
      api.get<{ data: GalleryCategory[] }>('/content/gallery-categories', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const categoryList = categories.data?.data ?? [];

  const syncSettingsDraft = () => {
    if (!gallery.data) return;
    setDraftTitle(gallery.data.title);
    setDraftCategoryId(gallery.data.categoryId ?? '');
    setDraftPublishedOn(gallery.data.publishedOn);
  };

  useEffect(() => {
    if (settingsOpen && gallery.data) {
      syncSettingsDraft();
    }
  }, [settingsOpen, gallery.data]);

  const photos = useQuery({
    queryKey: ['content', 'galleries', galleryId, 'photos'],
    queryFn: () => api.get<{ data: GalleryPhoto[] }>(`/content/galleries/${galleryId}/photos`),
    enabled: Boolean(galleryId),
  });

  const invalidateGalleryQueries = () => {
    void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
    void queryClient.invalidateQueries({ queryKey: ['content', 'galleries', galleryId] });
    void queryClient.invalidateQueries({ queryKey: ['content', 'galleries', galleryId, 'photos'] });
  };

  const updateGallery = useMutation({
    mutationFn: (data: {
      title?: string;
      publishedOn?: string;
      categoryId?: string | null;
      coverPhotoId?: string | null;
    }) => api.patch<Gallery>(`/content/galleries/${galleryId}`, data),
    onSuccess: (_, variables) => {
      invalidateGalleryQueries();
      if (variables.coverPhotoId !== undefined) {
        toast.success(t('galleries.detail.coverSet'));
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveSettings = useMutation({
    mutationFn: () =>
      api.patch<Gallery>(`/content/galleries/${galleryId}`, {
        title: draftTitle.trim(),
        categoryId: draftCategoryId || null,
        publishedOn: draftPublishedOn,
      }),
    onSuccess: () => {
      toast.success(t('galleries.settings.saved'));
      setSettingsOpen(false);
      invalidateGalleryQueries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publishGallery = useMutation({
    mutationFn: () => api.post<Gallery>(`/content/galleries/${galleryId}/publish`),
    onSuccess: () => {
      toast.success(t('galleries.published'));
      setPublishOpen(false);
      invalidateGalleryQueries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublishGallery = useMutation({
    mutationFn: () => api.post<Gallery>(`/content/galleries/${galleryId}/unpublish`),
    onSuccess: () => {
      toast.success(t('galleries.unpublished'));
      setUnpublishOpen(false);
      invalidateGalleryQueries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeGallery = useMutation({
    mutationFn: () => api.delete(`/content/galleries/${galleryId}`),
    onSuccess: () => {
      toast.success(t('galleries.deleted'));
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
      void navigate('/content/galleries');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removePhoto = useMutation({
    mutationFn: (photoId: string) =>
      api.delete(`/content/galleries/${galleryId}/photos/${photoId}`),
    onSuccess: () => {
      toast.success(t('galleries.detail.removed'));
      setRemoveId(null);
      setLightboxIndex(null);
      invalidateGalleryQueries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const uploadPhotos = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    let added = 0;
    let failed = 0;
    let tooLarge = 0;
    let invalid = 0;

    try {
      for (const [index, file] of files.entries()) {
        try {
          const { blob, fileName } = await compressGalleryPhoto(file);
          const { key } = await uploadBlob({
            purpose: 'gallery_photo',
            blob,
            fileName,
          });
          await api.post(`/content/galleries/${galleryId}/photos`, { imageKey: key });
          added += 1;
        } catch (error) {
          if (error instanceof GalleryPhotoTooLargeError) {
            tooLarge += 1;
          } else if (error instanceof GalleryPhotoInvalidError) {
            invalid += 1;
          } else {
            failed += 1;
          }
        }
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }

      invalidateGalleryQueries();

      if (added > 0) {
        toast.success(t('galleries.detail.uploaded', { count: added }));
      }
      if (tooLarge > 0) {
        toast.error(t('galleries.detail.uploadTooLarge', { count: tooLarge }));
      }
      if (invalid > 0) {
        toast.error(tErrors('GALLERY_PHOTO_INVALID'));
      }
      if (failed > 0 && added === 0 && tooLarge === 0 && invalid === 0) {
        toast.error(tErrors('UNEXPECTED'));
      } else if (failed > 0) {
        toast.error(t('galleries.detail.uploadPartialFailed', { count: failed }));
      }
    } finally {
      setUploading(false);
      setUploadProgress(undefined);
    }
  };

  const rows = photos.data?.data ?? [];
  const isLoading = gallery.isLoading || photos.isLoading;

  const isCoverPhoto = (photoId: string) => {
    if (gallery.data?.coverPhotoId) return gallery.data.coverPhotoId === photoId;
    return rows[0]?.id === photoId;
  };

  const lightboxPhotos = useMemo(
    () => rows.filter((photo) => Boolean(photo.imageUrl)),
    [rows],
  );

  const lightboxImages = useMemo(
    () => lightboxPhotos.map((photo) => photo.imageUrl!),
    [lightboxPhotos],
  );

  const photoIdToLightboxIndex = useMemo(() => {
    const map = new Map<string, number>();
    lightboxPhotos.forEach((photo, index) => {
      map.set(photo.id, index);
    });
    return map;
  }, [lightboxPhotos]);

  const lightboxCaption =
    lightboxIndex !== null && lightboxIndex >= 0 && lightboxIndex < lightboxPhotos.length
      ? lightboxPhotos[lightboxIndex]?.caption
      : null;

  if (gallery.isError) {
    return (
      <div>
        <PageHeader
          title={t('galleries.title')}
          breadcrumbs={[
            { label: t('galleries.title'), to: '/content/galleries' },
            { label: tCommon('state.errorTitle') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">
          {gallery.error instanceof ApiError
            ? tErrors(gallery.error.code, gallery.error.params)
            : tErrors('UNEXPECTED')}
        </p>
      </div>
    );
  }

  if (!gallery.data) {
    return (
      <div>
        <PageHeader
          title={t('galleries.title')}
          breadcrumbs={[
            { label: t('galleries.title'), to: '/content/galleries' },
            { label: tCommon('state.loading') },
          ]}
        />
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const photoCount = gallery.data.photoCount;
  const subtitle = gallery.data.categoryName
    ? t('galleries.detail.subtitleWithCategory', {
        count: photoCount,
        category: gallery.data.categoryName,
        date: formatDate(gallery.data.publishedOn),
      })
    : t('galleries.detail.subtitle', {
        count: photoCount,
        date: formatDate(gallery.data.publishedOn),
      });

  const publishHint =
    gallery.data.status === 'published'
      ? t('galleries.settings.publishedHint')
      : photoCount === 0
        ? t('galleries.settings.publishRequiresPhotos')
        : t('galleries.settings.draftHint');

  const canSaveSettings =
    draftTitle.trim().length > 0 &&
    Boolean(draftPublishedOn) &&
    (draftTitle.trim() !== gallery.data.title ||
      (draftCategoryId || null) !== gallery.data.categoryId ||
      draftPublishedOn !== gallery.data.publishedOn);

  return (
    <div>
      <PageHeader
        title={gallery.data.title}
        description={subtitle}
        breadcrumbs={[
          { label: t('galleries.title'), to: '/content/galleries' },
          { label: gallery.data.title },
        ]}
        headerActions={
          <div className="flex items-center gap-2">
            {gallery.data.canDelete === true ? (
              <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                <Button type="button" variant="outline" onClick={() => setDeleteOpen(true)}>
                  {tCommon('action.delete')}
                </Button>
              </PermissionGate>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('galleries.settings.open')}
              onClick={() => {
                syncSettingsDraft();
                setSettingsOpen(true);
              }}
            >
              <Settings className="size-4" aria-hidden />
            </Button>
            {gallery.data.photoCount > 0 ? (
              <GalleryAddPhotosButton
                disabled={uploading}
                label={t('galleries.detail.addPhotos')}
                onFiles={(files) => void uploadPhotos(files)}
              />
            ) : null}
          </div>
        }
      />

      {gallery.data.status !== 'published' ? (
        <div className="mb-6 border-b border-border pb-5">
          <Badge variant="neutral">{t(`status.${gallery.data.status}`)}</Badge>
        </div>
      ) : (
        <div className="mb-6" />
      )}

      <FormDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        title={t('galleries.settings.title')}
        size="md"
        submitLabel={tCommon('action.save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={saveSettings.isPending}
        submitDisabled={!canSaveSettings}
        onSubmit={() => saveSettings.mutate()}
        footerLeading={
          gallery.data.status === 'published' ? (
            <Button
              type="button"
              variant="secondary"
              disabled={saveSettings.isPending}
              onClick={() => setUnpublishOpen(true)}
            >
              {t('galleries.unpublish')}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={photoCount === 0 || saveSettings.isPending}
              onClick={() => setPublishOpen(true)}
            >
              {t('galleries.publish')}
            </Button>
          )
        }
      >
        <FormField label={t('galleries.titleLabel')} required>
          {(field) => (
            <Input
              id={field.id}
              value={draftTitle}
              onChange={(event) => setDraftTitle(event.target.value)}
              aria-describedby={field['aria-describedby']}
              aria-invalid={field.invalid}
            />
          )}
        </FormField>

        <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
          <FormField label={t('galleries.categoryLabel')} className="min-w-0 flex-1">
            {(field) => (
              <Listbox
                id={field.id}
                value={draftCategoryId}
                disabled={categoryList.length === 0}
                onChange={(value) => setDraftCategoryId(value ?? '')}
                options={categoryList.map((item) => ({ value: item.id, label: item.name }))}
                popoverPortal="dialog"
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>

          <FormField label={t('galleries.publishedOnLabel')} required className="min-w-0 flex-1">
            {(field) => (
              <DatePicker
                id={field.id}
                value={draftPublishedOn}
                onChange={(date) => date && setDraftPublishedOn(date)}
                placeholder={t('galleries.publishedOnLabel')}
                popoverPortal="dialog"
                aria-describedby={field['aria-describedby']}
              />
            )}
          </FormField>
        </div>

        <div className="border-t border-border pt-6">
          <p className="mb-3 text-body-sm font-medium text-fg">{t('galleries.settings.publishing')}</p>
          <div className="flex items-center gap-3">
            <Badge variant={gallery.data.status === 'published' ? 'success' : 'neutral'}>
              {gallery.data.status === 'published'
                ? t('galleries.status.published')
                : t(`status.${gallery.data.status}`)}
            </Badge>
            <p className="text-body-sm text-fg-secondary">{publishHint}</p>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={t('confirm.publishGallery.title', { name: gallery.data.title })}
        description={t('confirm.publishGallery.description')}
        confirmLabel={t('confirm.publishGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publishGallery.isPending}
        onConfirm={() => publishGallery.mutate()}
      />

      <ConfirmDialog
        open={unpublishOpen}
        onOpenChange={setUnpublishOpen}
        title={t('confirm.unpublishGallery.title', { name: gallery.data.title })}
        description={t('confirm.unpublishGallery.description')}
        confirmLabel={t('confirm.unpublishGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublishGallery.isPending}
        onConfirm={() => unpublishGallery.mutate()}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('confirm.deleteGallery.title', { name: gallery.data.title })}
        description={t('confirm.deleteGallery.description')}
        confirmLabel={t('confirm.deleteGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeGallery.isPending}
        onConfirm={() => removeGallery.mutate()}
      />

      {uploading && uploadProgress !== undefined ? (
        <ProgressBar
          value={uploadProgress}
          label={t('galleries.detail.uploading')}
          className="mb-4 max-w-sm"
          size="sm"
        />
      ) : null}

      {isLoading ? (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="mx-auto max-w-md">
          <FileDropzone
            multiple
            accept={PHOTO_ACCEPT}
            acceptLabel={t('galleries.detail.uploadAccept')}
            maxSizeLabel={t('galleries.detail.uploadMaxSize')}
            disabled={uploading}
            progress={uploadProgress}
            progressLabel={uploading ? t('galleries.detail.uploading') : undefined}
            labels={{
              prompt: t('galleries.detail.uploadPrompt'),
              remove: tCommon('action.remove'),
            }}
            onFiles={(files) => void uploadPhotos(files)}
          />
        </div>
      ) : (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {rows.map((photo) => {
            const previewUrl = photo.thumbnailUrl ?? photo.imageUrl;
            const isCover = isCoverPhoto(photo.id);
            return (
              <li key={photo.id} className="group relative aspect-square overflow-hidden rounded-lg">
                <div
                  className={cn(
                    'absolute inset-0 bg-surface-muted',
                    isCover && 'ring-2 ring-inset ring-brand',
                  )}
                  style={{ backgroundColor: photo.mainColor ?? undefined }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={photo.caption ?? ''}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                </div>
                <button
                  type="button"
                  className="absolute inset-0 z-10 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                  aria-label={t('galleries.detail.previewPhoto')}
                  onClick={() => {
                    const index = photoIdToLightboxIndex.get(photo.id);
                    if (index !== undefined) setLightboxIndex(index);
                  }}
                />
                {isCover ? (
                  <Badge variant="brand" className="pointer-events-none absolute start-2 top-2 z-20 shadow-sm">
                    {t('galleries.detail.cover')}
                  </Badge>
                ) : null}
                <div
                  className={cn(
                    'absolute inset-x-0 bottom-0 z-20 flex items-center justify-end gap-1 p-2',
                    'bg-gradient-to-t from-black/70 via-black/40 to-transparent',
                    'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100',
                  )}
                >
                  {!isCover ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 text-white hover:bg-white/15"
                      leadingIcon={<ImageIcon className="size-3.5" aria-hidden />}
                      disabled={updateGallery.isPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        updateGallery.mutate({ coverPhotoId: photo.id });
                      }}
                    >
                      {t('galleries.detail.setCover')}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 shrink-0 text-white hover:bg-white/15"
                    aria-label={t('galleries.detail.remove')}
                    onClick={(event) => {
                      event.stopPropagation();
                      setRemoveId(photo.id);
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ImageLightbox
        images={lightboxImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        caption={lightboxCaption}
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count:
            lightboxIndex !== null
              ? tCommon('lightbox.imageCount', {
                  current: lightboxIndex + 1,
                  total: lightboxImages.length,
                })
              : '',
          imageAlt:
            lightboxIndex !== null
              ? t('galleries.detail.previewImageAlt', {
                  title: gallery.data.title,
                  current: lightboxIndex + 1,
                  total: lightboxImages.length,
                })
              : '',
        }}
      />

      <ConfirmDialog
        open={removeId !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveId(null);
        }}
        title={t('galleries.detail.removeConfirm.title')}
        description={t('galleries.detail.removeConfirm.description')}
        confirmLabel={t('galleries.detail.removeConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removePhoto.isPending}
        onConfirm={() => {
          if (removeId) removePhoto.mutate(removeId);
        }}
      />
    </div>
  );
};
